import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  alpha,
  Stack,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  LocalHospital as HospitalIcon,
  Security as PoliceIcon,
  AccountBalance as GovernmentIcon,
  VolunteerActivism as NonProfitIcon,
  MilitaryTech as VeteranIcon,
  Engineering as InfrastructureIcon,
  Check as CheckIcon,
  Mail as ContactIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';

const SpecialPricingPage = () => {
  const theme = useTheme();

  const eligibleOrganizations = [
    {
      title: 'Municipal Government',
      icon: <GovernmentIcon sx={{ fontSize: 40 }} />,
      description: 'City, county, and local government agencies protecting citizen data and municipal systems.',
      examples: [
        'City and county governments',
        'Municipal utilities',
        'Public transportation authorities',
        'Municipal courts and clerks',
        'Local government IT departments',
      ],
      discount: '50%',
      color: theme.palette.accent?.main || theme.palette.primary.main,
    },
    {
      title: 'Healthcare Systems',
      icon: <HospitalIcon sx={{ fontSize: 40 }} />,
      description: 'Hospitals, clinics, and healthcare organizations protecting sensitive patient information.',
      examples: [
        'Public hospitals and health systems',
        'Community health centers',
        'Emergency medical services',
        'Public health departments',
        'Mental health facilities',
      ],
      discount: '50%',
      color: theme.palette.error.main,
    },
    {
      title: 'Emergency Services',
      icon: <PoliceIcon sx={{ fontSize: 40 }} />,
      description: 'Police, fire, and emergency response teams securing critical communications and data.',
      examples: [
        'Police departments',
        'Fire departments',
        'Emergency management agencies',
        '911 dispatch centers',
        'Search and rescue organizations',
      ],
      discount: '50%',
      color: theme.palette.warning.main,
    },
    {
      title: 'Veterans Organizations',
      icon: <VeteranIcon sx={{ fontSize: 40 }} />,
      description: 'Organizations serving veterans and military families with secure information management.',
      examples: [
        'Veterans Affairs offices',
        'Veterans service organizations',
        'Military family support groups',
        'Veteran healthcare facilities',
        'Military transition programs',
      ],
      discount: '50%',
      color: theme.palette.secondary.main,
    },
    {
      title: 'Non-Profit Organizations',
      icon: <NonProfitIcon sx={{ fontSize: 40 }} />,
      description: 'Registered 501(c)(3) organizations working for the public good.',
      examples: [
        'Charitable foundations',
        'Community service organizations',
        'Religious institutions',
        'Educational non-profits',
        'Environmental organizations',
      ],
      discount: '40%',
      color: theme.palette.success.main,
    },
    {
      title: 'Critical Infrastructure',
      icon: <InfrastructureIcon sx={{ fontSize: 40 }} />,
      description: 'Organizations maintaining essential public services and infrastructure.',
      examples: [
        'Public utilities (water, power, gas)',
        'Public transportation systems',
        'Public works departments',
        'Waste management services',
        'Public housing authorities',
      ],
      discount: '45%',
      color: theme.palette.info.main,
    },
  ];

  const qualificationRequirements = [
    'Valid government or non-profit registration documentation',
    'Official email address from qualifying organization',
    'Proof of tax-exempt status (for non-profits)',
    'Minimum commitment of 12 months',
    'Agreement to use PWGen-Rust for official organizational purposes',
  ];

  return (
    <Box>
      <PageHeader
        title="Special Pricing for Essential Services"
        subtitle="Supporting the organizations that keep our communities safe and secure"
        backgroundImage="/img/12.webp"
        showBreadcrumbs={true}
        tags={['Pricing', 'Discounts', 'Community']}
      />

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Overview Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Protecting Those Who Protect Us
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
            PWGen-Rust recognizes the critical role that municipalities, healthcare systems, emergency services, 
            and non-profit organizations play in our communities. We're committed to providing secure password 
            management solutions at accessible pricing for these essential services.
          </Typography>
        </Box>

        {/* Eligible Organizations Grid */}
        <Typography variant="h4" fontWeight="bold" textAlign="center" sx={{ mb: 6 }}>
          Eligible Organizations
        </Typography>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {eligibleOrganizations.map((org, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: org.color, mr: 2 }}>
                      {org.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {org.title}
                      </Typography>
                      <Chip
                        label={`${org.discount} OFF`}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {org.description}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Examples include:
                  </Typography>
                  <List dense>
                    {org.examples.map((example, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon sx={{ fontSize: 16, color: theme.palette.accent?.main || theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={example}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Qualification Requirements */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 8, 
            border: '1px solid', 
            borderColor: 'divider',
            backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            Qualification Requirements
          </Typography>
          
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }}>
              <List>
                {qualificationRequirements.map((requirement, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: theme.palette.accent?.main || theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={requirement}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <DocumentIcon sx={{ fontSize: 64, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Simple Application
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Quick verification process with minimal documentation required
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Application Process */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            How to Apply
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Getting special pricing is straightforward. Contact our team with your organization details 
            and qualifying documentation for quick approval.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<ContactIcon />}
              href="mailto:pricing@kief.studio"
              sx={{
                backgroundColor: theme.palette.accent?.main || theme.palette.primary.main,
                color: 'white',
                py: 1.5,
                px: 4,
                '&:hover': {
                  backgroundColor: theme.palette.accent?.dark || theme.palette.primary.dark,
                },
              }}
            >
              Apply Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="/contact"
              sx={{
                borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                color: theme.palette.accent?.main || theme.palette.primary.main,
                py: 1.5,
                px: 4,
                '&:hover': {
                  borderColor: theme.palette.accent?.dark || theme.palette.primary.dark,
                  backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.08),
                },
              }}
            >
              Contact Us
            </Button>
          </Stack>
        </Box>

        {/* Footer Note */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.05),
            border: '1px solid',
            borderColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.2),
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Special pricing is available for qualifying organizations only. 
            Commercial use requires standard pricing. Processing typically takes 2-3 business days. 
            Discounts apply to Business and Professional plans when available.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default SpecialPricingPage;